/**
 * FloorPlanCanvas - Fabric.js canvas with floor plan background + room shapes
 *
 * Handles:
 * - Loading floor plan image as background
 * - Creating room shapes from AI bounding boxes
 * - Zoom (scroll wheel), pan (Alt+drag), selection
 * - Shape move/resize events
 * - Drawing mode for new rectangles
 */

import { useEffect, useRef, useCallback } from 'react';
import { Canvas, Rect, Polygon as FabricPolygon, FabricText, FabricImage, config } from 'fabric';
import styles from './FloorPlanEditor.module.css';

// Increase cache limits for large retina floor plans
config.perfLimitSizeTotal = 4194304;
config.maxCacheSideLimit = 8192;

// Status → color mapping
const STATUS_COLORS = {
  new: { fill: '#22c55e4d', stroke: '#22c55e' },
  matched: { fill: '#3b82f64d', stroke: '#3b82f6' },
  size_change: { fill: '#f59e0b4d', stroke: '#f59e0b' },
};

/**
 * Create a Fabric Rect + label from a space object
 */
/**
 * Create a shape from a space, in IMAGE pixel coordinates.
 * The viewport transform handles scaling and centering on the canvas.
 */
function createShapeFromSpace(space, imgWidth, imgHeight) {
  const bbox = space.bounding_box;
  const polygon = space.polygon;
  if (!bbox && !polygon) return null;

  const colors = STATUS_COLORS[space.status] || STATUS_COLORS.new;

  let shape;
  let labelX, labelY;

  if (polygon && polygon.length >= 3) {
    // Polygon mode: CV pipeline returned precise room boundaries
    // Polygon coords are in 0-1000 normalized space → convert to image pixels
    const points = polygon.map(([nx, ny]) => ({
      x: (nx / 1000) * imgWidth,
      y: (ny / 1000) * imgHeight,
    }));

    shape = new FabricPolygon(points, {
      fill: colors.fill,
      stroke: colors.stroke,
      strokeWidth: 2,
      lockRotation: true,
      noScaleCache: true,
      cornerColor: colors.stroke,
      cornerSize: 8,
      transparentCorners: false,
      objectCaching: false,
    });
  } else if (bbox) {
    // Rectangle fallback: Gemini bounding box in 0-1000 normalized space
    const pixelX = (bbox.x / 1000) * imgWidth;
    const pixelY = (bbox.y / 1000) * imgHeight;
    const pixelW = (bbox.width / 1000) * imgWidth;
    const pixelH = (bbox.height / 1000) * imgHeight;

    shape = new Rect();
    shape.set({
      left: pixelX,
      top: pixelY,
      width: pixelW,
      height: pixelH,
    });
  } else {
    return null;
  }

  shape.set({
    fill: colors.fill,
    stroke: colors.stroke,
    strokeWidth: 2,
    lockRotation: true,
    noScaleCache: true,
    cornerColor: colors.stroke,
    cornerSize: 8,
    transparentCorners: false,
  });

  shape.spaceData = {
    ...space,
    included: true,
    _originalNameHe: space.suggested_name_he || '',
  };

  // Compute label position from the shape's actual center (works for both poly + rect)
  shape.setCoords();
  const center = shape.getCenterPoint();
  labelX = center.x;
  labelY = center.y;

  const displayName = space.suggested_name_he || space.suggested_name || '';
  const sizeText = space.estimated_size_sqm ? `${space.estimated_size_sqm.toFixed(1)} מ"ר` : '';
  const label = new FabricText(`${displayName}\n${sizeText}`, {});
  label.set({
    left: labelX,
    top: labelY,
    originX: 'center',
    originY: 'center',
    fontSize: 14,
    fill: '#1e293b',
    fontFamily: 'Arial, sans-serif',
    selectable: false,
    evented: false,
    textAlign: 'center',
  });

  shape.label = label;
  label.parentRect = shape;

  return { rect: shape, label };
}

/**
 * Bake scaleX/scaleY into a Fabric polygon's points array, then reset scale.
 * Fabric polygons store transforms separately from geometry; on save we need
 * the actual vertex positions, so we apply the scale and reset to 1.
 */
function bakePolygonScale(poly) {
  if (poly.type !== 'polygon' || !poly.points) return;
  const sx = poly.scaleX || 1;
  const sy = poly.scaleY || 1;
  // Find polygon's local pivot (top-left of bounding box of points)
  const xs = poly.points.map(p => p.x);
  const ys = poly.points.map(p => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  // Scale points around the polygon's local origin
  poly.points = poly.points.map(p => ({
    x: minX + (p.x - minX) * sx,
    y: minY + (p.y - minY) * sy,
  }));
  // Recompute pathOffset / dimensions, reset scale
  poly.scaleX = 1;
  poly.scaleY = 1;
  poly._setPositionDimensions?.({});
  poly.setCoords?.();
}

/**
 * Compute the area of a Fabric shape in pixel² (image-pixel space).
 * For polygons uses the shoelace formula on the points (with scale baked in).
 * For rectangles uses width × height × scale.
 */
function computeShapeAreaPx(obj) {
  if (obj.type === 'polygon' && obj.points && obj.points.length >= 3) {
    const sx = obj.scaleX || 1;
    const sy = obj.scaleY || 1;
    // Shoelace formula
    let sum = 0;
    const n = obj.points.length;
    for (let i = 0; i < n; i++) {
      const a = obj.points[i];
      const b = obj.points[(i + 1) % n];
      sum += a.x * b.y - b.x * a.y;
    }
    return Math.abs(sum / 2) * sx * sy;
  }
  // Rectangles and other shapes
  return (obj.width || 0) * (obj.scaleX || 1) * (obj.height || 0) * (obj.scaleY || 1);
}

/**
 * Sync label position after shape move/resize.
 * Uses the shape's actual center point so the label tracks the geometry,
 * not the bounding-box top-left (which is unreliable for polygons).
 */
function syncLabel(rect) {
  if (!rect.label) return;
  const center = rect.getCenterPoint();
  // Position label slightly above center, accounting for label height
  const label = rect.label;
  label.set({
    left: center.x,
    top: center.y,
    originX: 'center',
    originY: 'center',
  });
  label.setCoords?.();
}

/**
 * Update label text from spaceData
 */
function updateLabelText(rect) {
  if (!rect.label) return;
  const d = rect.spaceData;
  const name = d.suggested_name_he || d.suggested_name || '';
  const size = d.estimated_size_sqm ? `${d.estimated_size_sqm.toFixed(1)} מ"ר` : '';
  rect.label.set({ text: `${name}\n${size}` });
}

export default function FloorPlanCanvas({
  imageUrl,
  spaces,
  metadata,
  onShapeSelected,
  onShapeModified,
  onShapeAdded,
  onEditorReady,
  drawingMode,
  panMode,
}) {
  const containerRef = useRef(null);
  const canvasElRef = useRef(null);
  const fabricRef = useRef(null);
  const shapesRef = useRef([]);
  const imageRef = useRef(null);
  // Pixels-per-meter in DISPLAYED-image coordinate space. Computed once
  // when the image loads (CV's px/m is in CV-resized space, so we scale it).
  // Stored in a ref so the object:modified handler reads the current value
  // without re-binding the closure.
  const ppmRef = useRef(null);

  // Initialize canvas
  useEffect(() => {
    if (!canvasElRef.current) return;

    const container = containerRef.current;
    const canvas = new Canvas(canvasElRef.current, {
      renderOnAddRemove: false,
      backgroundColor: '#f1f5f9',
    });
    fabricRef.current = canvas;

    // Set initial size
    if (container) {
      canvas.setDimensions({
        width: container.clientWidth,
        height: container.clientHeight,
      });
    }

    // Selection events
    canvas.on('selection:created', e => {
      const obj = e.selected?.[0];
      if (obj?.spaceData) onShapeSelected?.(obj);
    });
    canvas.on('selection:updated', e => {
      const obj = e.selected?.[0];
      if (obj?.spaceData) onShapeSelected?.(obj);
    });
    canvas.on('selection:cleared', () => onShapeSelected?.(null));

    // Move/resize events — sync label + notify parent
    canvas.on('object:moving', e => syncLabel(e.target));
    canvas.on('object:scaling', e => syncLabel(e.target));
    canvas.on('object:modified', e => {
      const obj = e.target;
      if (!obj?.spaceData) return;

      // For polygons: bake scaleX/Y into the points array so the geometry
      // actually updates. Fabric polygons keep scale separate from points.
      if (obj.type === 'polygon' && (obj.scaleX !== 1 || obj.scaleY !== 1)) {
        bakePolygonScale(obj);
      }

      syncLabel(obj);

      // Recalculate area in sqm. ppmRef holds the pixels-per-meter in the
      // DISPLAYED image's coordinate space (computed at image load).
      const ppm = ppmRef.current;
      if (ppm) {
        const areaPx = computeShapeAreaPx(obj);
        obj.spaceData.estimated_size_sqm = parseFloat((areaPx / (ppm * ppm)).toFixed(1));
        updateLabelText(obj);
      }
      onShapeModified?.(obj);
    });

    // Zoom via scroll wheel
    canvas.on('mouse:wheel', opt => {
      const delta = opt.e.deltaY;
      let zoom = canvas.getZoom();
      zoom *= 0.999 ** delta;
      zoom = Math.min(Math.max(zoom, 0.1), 20);
      canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    });

    // Pan via Alt+drag
    let isDragging = false;
    let lastPosX = 0;
    let lastPosY = 0;

    canvas.on('mouse:down', opt => {
      if (opt.e.altKey || canvas._panMode) {
        isDragging = true;
        canvas.selection = false;
        lastPosX = opt.e.clientX;
        lastPosY = opt.e.clientY;
      }
    });
    canvas.on('mouse:move', opt => {
      if (isDragging) {
        const vpt = canvas.viewportTransform;
        vpt[4] += opt.e.clientX - lastPosX;
        vpt[5] += opt.e.clientY - lastPosY;
        canvas.requestRenderAll();
        lastPosX = opt.e.clientX;
        lastPosY = opt.e.clientY;
      }
    });
    canvas.on('mouse:up', () => {
      if (isDragging) {
        canvas.setViewportTransform(canvas.viewportTransform);
        isDragging = false;
        canvas.selection = true;
      }
    });

    // Drawing mode — create new rect on mouse:up
    let drawStart = null;
    let drawRect = null;

    canvas.on('mouse:down', opt => {
      if (!canvas.isDrawingMode || isDragging) return;
      const pointer = canvas.getScenePoint(opt.e);
      drawStart = { x: pointer.x, y: pointer.y };
      drawRect = new Rect();
      drawRect.set({
        left: pointer.x,
        top: pointer.y,
        width: 0,
        height: 0,
        fill: '#22c55e4d',
        stroke: '#22c55e',
        strokeWidth: 2,
        lockRotation: true,
        noScaleCache: true,
        cornerColor: '#22c55e',
        cornerSize: 8,
        transparentCorners: false,
        selectable: false,
      });
      canvas.add(drawRect);
      canvas.requestRenderAll();
    });

    canvas.on('mouse:move', opt => {
      if (!drawStart || !drawRect) return;
      const pointer = canvas.getScenePoint(opt.e);
      const left = Math.min(drawStart.x, pointer.x);
      const top = Math.min(drawStart.y, pointer.y);
      const width = Math.abs(pointer.x - drawStart.x);
      const height = Math.abs(pointer.y - drawStart.y);
      drawRect.set({ left, top, width, height });
      canvas.requestRenderAll();
    });

    canvas.on('mouse:up', () => {
      if (!drawStart || !drawRect) return;
      const w = drawRect.width;
      const h = drawRect.height;
      if (w < 10 || h < 10) {
        // Too small — discard
        canvas.remove(drawRect);
      } else {
        drawRect.set({ selectable: true, evented: true });
        drawRect.spaceData = {
          suggested_name: '',
          suggested_name_he: '',
          suggested_type: 'Other',
          master_space_id: null,
          estimated_size_sqm: 0,
          confidence: 1,
          status: 'new',
          included: true,
          isUserDrawn: true,
        };

        const label = new FabricText('חלל חדש', {});
        label.set({
          left: drawRect.left + 6,
          top: drawRect.top + 4,
          fontSize: 12,
          fill: '#1e293b',
          fontFamily: 'Arial, sans-serif',
          selectable: false,
          evented: false,
        });
        drawRect.label = label;
        label.parentRect = drawRect;
        canvas.add(label);

        shapesRef.current.push(drawRect);
        onShapeAdded?.(drawRect);
        canvas.setActiveObject(drawRect);
      }
      canvas.requestRenderAll();
      drawStart = null;
      drawRect = null;
    });

    // ResizeObserver
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) {
        canvas.setDimensions({ width, height });
        canvas.requestRenderAll();
      }
    });
    if (container) ro.observe(container);

    return () => {
      ro.disconnect();
      canvas.dispose();
      fabricRef.current = null;
      shapesRef.current = [];
      imageRef.current = null;
      ppmRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  // Load background image + shapes when imageUrl or spaces change
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !imageUrl) return;

    let cancelled = false;

    async function loadImage() {
      try {
        const img = await FabricImage.fromURL(imageUrl, { crossOrigin: 'anonymous' });
        if (cancelled) return;

        // Re-read canvas dimensions NOW (in case container has resized since init)
        const container = containerRef.current;
        if (container) {
          canvas.setDimensions({
            width: container.clientWidth,
            height: container.clientHeight,
          });
        }

        // Add image as a regular locked scene object (not backgroundImage).
        // This guarantees the image and shapes share the same coordinate space
        // and are both transformed by viewportTransform consistently.
        img.set({
          left: 0,
          top: 0,
          originX: 'left',
          originY: 'top',
          selectable: false,
          evented: false,
          hoverCursor: 'default',
        });

        // Clear all existing scene objects (shapes + previous image if any)
        canvas.remove(...canvas.getObjects());
        canvas.backgroundImage = undefined;
        shapesRef.current = [];

        // Add the image and send it to the back
        canvas.add(img);
        canvas.sendObjectToBack(img);
        imageRef.current = img;

        // Compute pixels-per-meter in the DISPLAYED image's coordinate space.
        //
        // The CV service computes ppm in its own resized coordinate space (max 2048),
        // so we have to scale it by (display_width / cv_image_width) to get the
        // ratio that's correct for the image as Fabric is rendering it.
        //
        // Falls back to a paper-size estimate (A1 portrait) if no CV scale is
        // available — imperfect but better than nothing for plans without
        // OCR-readable dimensions.
        let pixelsPerMeter = null;
        if (metadata?.cv_scale_px_per_m && metadata?.cv_image_width) {
          const cvToDisplayRatio = img.width / metadata.cv_image_width;
          pixelsPerMeter = metadata.cv_scale_px_per_m * cvToDisplayRatio;
        } else if (metadata?.scale_used) {
          const match = metadata.scale_used.match(/1:(\d+)/);
          if (match) {
            const scaleRatio = parseInt(match[1], 10);
            const paperWidthM = 0.841; // A1 paper width fallback
            const realWidthM = paperWidthM * scaleRatio;
            pixelsPerMeter = img.width / realWidthM;
          }
        }
        // Store in ref so the object:modified handler can read the latest value
        // without re-binding closures or mutating the metadata prop.
        ppmRef.current = pixelsPerMeter;

        // Create shapes in image pixel coordinates
        if (spaces?.length) {
          const shapes = [];
          for (const space of spaces) {
            const result = createShapeFromSpace(space, img.width, img.height);
            if (result) {
              canvas.add(result.rect);
              canvas.add(result.label);
              shapes.push(result.rect);
            }
          }
          shapesRef.current = shapes;
        }

        // Use viewport transform to center and fit image (and shapes) on canvas
        const fitScale = Math.min(canvas.width / img.width, canvas.height / img.height) * 0.95;
        const offsetX = (canvas.width - img.width * fitScale) / 2;
        const offsetY = (canvas.height - img.height * fitScale) / 2;
        canvas.setViewportTransform([fitScale, 0, 0, fitScale, offsetX, offsetY]);

        canvas.requestRenderAll();
      } catch (err) {
        console.error('Failed to load floor plan image:', err);
      }
    }

    loadImage();
    return () => {
      cancelled = true;
    };
  }, [imageUrl, spaces, metadata]);

  // Toggle pan mode
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas._panMode = panMode;
    canvas.defaultCursor = panMode ? 'grab' : 'default';
    canvas.skipTargetFind = panMode;
  }, [panMode]);

  // Toggle drawing mode
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = drawingMode;
    canvas.defaultCursor = drawingMode ? 'crosshair' : panMode ? 'grab' : 'default';
    if (drawingMode) {
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    }
  }, [drawingMode, panMode]);

  // Public methods via ref
  const selectShape = useCallback(rect => {
    const canvas = fabricRef.current;
    if (!canvas || !rect) return;
    canvas.setActiveObject(rect);
    // Center the view on the shape
    const center = rect.getCenterPoint();
    const vpt = canvas.viewportTransform;
    const zoom = canvas.getZoom();
    vpt[4] = canvas.width / 2 - center.x * zoom;
    vpt[5] = canvas.height / 2 - center.y * zoom;
    canvas.setViewportTransform(vpt);
    canvas.requestRenderAll();
  }, []);

  const deleteShape = useCallback(
    rect => {
      const canvas = fabricRef.current;
      if (!canvas || !rect) return;
      if (rect.label) canvas.remove(rect.label);
      canvas.remove(rect);
      shapesRef.current = shapesRef.current.filter(s => s !== rect);
      canvas.requestRenderAll();
      onShapeSelected?.(null);
    },
    [onShapeSelected],
  );

  const fitToScreen = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const img = imageRef.current;
    if (!img) {
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      canvas.requestRenderAll();
      return;
    }
    const fitScale = Math.min(canvas.width / img.width, canvas.height / img.height) * 0.95;
    const offsetX = (canvas.width - img.width * fitScale) / 2;
    const offsetY = (canvas.height - img.height * fitScale) / 2;
    canvas.setViewportTransform([fitScale, 0, 0, fitScale, offsetX, offsetY]);
    canvas.requestRenderAll();
  }, []);

  const getShapes = useCallback(() => shapesRef.current, []);

  // Expose methods to parent via callback
  useEffect(() => {
    onEditorReady?.({
      selectShape,
      deleteShape,
      fitToScreen,
      getShapes,
      getCanvas: () => fabricRef.current,
    });
  }, [selectShape, deleteShape, fitToScreen, getShapes, onEditorReady]);

  return (
    <div className={styles.canvasArea} ref={containerRef}>
      <div className={styles.canvasContainer}>
        <canvas ref={canvasElRef} />
      </div>
    </div>
  );
}
